import { ProductOption } from '../apis/models/products/res/ProductOption';
import {
  ProductOptionAvailabilityStatus,
  ProductOptionValidationRow,
  ValidatedProductOption,
} from '../apis/models/products/res/ProductOptionValidation';
import { ProductOptionValidationItem } from '../apis/models/products/req/ValidateProductOptionsBody';

const AVAILABILITY_STATUSES: ProductOptionAvailabilityStatus[] = [
  'AVAILABLE',
  'NOT_FOUND',
  'DELETED',
  'NOT_IN_PRICE_SHEET',
  'HIDDEN',
  'EXPIRED',
  'SECRET_NOT_ALLOWED',
];

export class InvalidProductOptionValidationResponseError extends Error {
  constructor(message: string) {
    super(`Invalid product option validation response: ${message}`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || typeof value === 'number';
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

/** 선택 목록 → 검증 API body 항목. 기획전이 없는 옵션은 키를 생략한다. */
export function toProductOptionValidationItems(
  productOptions: ProductOption[],
): ProductOptionValidationItem[] {
  return productOptions.map(({ id, exhibitionId }) =>
    exhibitionId == null
      ? { productOptionId: id }
      : { productOptionId: id, exhibitionId },
  );
}

/** 선택 스냅샷 키. 순서와 옵션별 기획전 아이디를 포함한다. */
export function getProductOptionSnapshotKey(productOptions: ProductOption[]) {
  return productOptions
    .map(({ id, exhibitionId }) => `${id}:${exhibitionId ?? ''}`)
    .join(',');
}

function isValidatedProductOption(
  value: unknown,
  expectedId: number,
): value is ValidatedProductOption {
  if (!isRecord(value)) return false;
  return (
    value.id === expectedId &&
    typeof value.productOptionName === 'string' &&
    (value.productOptionDescription === undefined ||
      isNullableString(value.productOptionDescription)) &&
    typeof value.priceViewable === 'boolean' &&
    isNullableNumber(value.productOptionPrice) &&
    isNullableNumber(value.productOptionTax) &&
    isNullableNumber(value.productOptionTotalPrice) &&
    isNullableNumber(value.productOptionDiscountPrice) &&
    isNullableNumber(value.productOptionDiscountTax) &&
    isNullableNumber(value.productOptionTotalDiscountPrice)
  );
}

/**
 * 검증 응답 엄격 검사. 항목 수·순서·ID·기획전·알려진 상태·AVAILABLE 필수 필드를 확인한다.
 * 불완전한 응답을 "삭제"로 해석하지 않도록 하나라도 어긋나면 던진다.
 */
export function parseProductOptionValidationRows(
  response: unknown,
  items: ProductOptionValidationItem[],
): ProductOptionValidationRow[] {
  if (!isRecord(response) || !Array.isArray(response.rows)) {
    throw new InvalidProductOptionValidationResponseError('rows');
  }
  if (
    response.count !== items.length ||
    response.rows.length !== items.length
  ) {
    throw new InvalidProductOptionValidationResponseError('count');
  }

  return response.rows.map((row, index) => {
    const requested = items[index];
    if (!isRecord(row)) {
      throw new InvalidProductOptionValidationResponseError('row');
    }
    if (row.productOptionId !== requested.productOptionId) {
      throw new InvalidProductOptionValidationResponseError('productOptionId');
    }
    if (row.exhibitionId !== (requested.exhibitionId ?? null)) {
      throw new InvalidProductOptionValidationResponseError('exhibitionId');
    }
    if (
      !AVAILABILITY_STATUSES.includes(
        row.status as ProductOptionAvailabilityStatus,
      )
    ) {
      throw new InvalidProductOptionValidationResponseError('status');
    }

    if (row.status === 'AVAILABLE') {
      if (
        !isValidatedProductOption(row.productOption, requested.productOptionId)
      ) {
        throw new InvalidProductOptionValidationResponseError('productOption');
      }
    } else if (row.productOption !== null) {
      throw new InvalidProductOptionValidationResponseError('productOption');
    }

    return row as unknown as ProductOptionValidationRow;
  });
}

/** 서버 소유 필드만 갱신한다. 기획전 아이디와 클라이언트 추가 GA 필드(상품명·카테고리 등)는 유지한다. */
export function mapValidatedProductOption(
  option: ProductOption,
  server: ValidatedProductOption,
): ProductOption {
  return {
    ...option,
    productOptionName: server.productOptionName,
    // 목록과 같이 설명이 없으면 빈 문자열로 저장한다
    productOptionDescription: server.productOptionDescription ?? '',
    priceViewable: server.priceViewable,
    // 목록 API도 빈 가격·세금을 null 그대로 내려주며 기존 금액 계산은 이를 0으로 처리한다.
    // 저장 타입은 목록과 같게 유지한다.
    productOptionPrice: server.productOptionPrice,
    productOptionDiscountPrice: server.productOptionDiscountPrice,
    productOptionTotalPrice: server.productOptionTotalPrice as number,
    productOptionTotalDiscountPrice: server.productOptionTotalDiscountPrice,
    productOptionTax: server.productOptionTax as number,
    productOptionDiscountTax: server.productOptionDiscountTax,
  };
}

/** 저장값이 모르는 값(undefined)이면 변경으로 보지 않는다. null과 0은 구분한다. */
function isSameKnownValue<T>(a: T | undefined, b: T | undefined) {
  return a === undefined || b === undefined || a === b;
}

function isSameKnownDescription(
  a: string | null | undefined,
  b: string | null | undefined,
) {
  if (a === undefined || b === undefined) return true;
  return (a ?? '') === (b ?? '');
}

/** 사용자에게 보이는 값과 예약 요청에 쓰이는 값이 같은지 비교한다. */
export function isSameProductOption(a: ProductOption, b: ProductOption) {
  return (
    isSameKnownValue(a.productOptionName, b.productOptionName) &&
    isSameKnownDescription(
      a.productOptionDescription,
      b.productOptionDescription,
    ) &&
    isSameKnownValue(a.priceViewable, b.priceViewable) &&
    isSameKnownValue(a.productOptionPrice, b.productOptionPrice) &&
    isSameKnownValue(
      a.productOptionDiscountPrice,
      b.productOptionDiscountPrice,
    ) &&
    isSameKnownValue(a.productOptionTotalPrice, b.productOptionTotalPrice) &&
    isSameKnownValue(
      a.productOptionTotalDiscountPrice,
      b.productOptionTotalDiscountPrice,
    )
    // 세금은 화면·예약 금액에 쓰이지 않고(GA 전용), 기획전 상세 매핑(apis/exhibitions.ts)이 0으로 고정 저장하므로
    // 변경 판정에서 제외한다. 값 차이는 refreshed로 조용히 채운다.
  );
}

/** 저장값을 서버 값으로 채워야 하는지(모르던 값 채움 포함) 확인한다. 변경 안내 대상은 아니다. */
function hasSameServerFields(a: ProductOption, b: ProductOption) {
  return (
    a.productOptionName === b.productOptionName &&
    a.productOptionDescription === b.productOptionDescription &&
    a.priceViewable === b.priceViewable &&
    a.productOptionPrice === b.productOptionPrice &&
    a.productOptionDiscountPrice === b.productOptionDiscountPrice &&
    a.productOptionTotalPrice === b.productOptionTotalPrice &&
    a.productOptionTotalDiscountPrice === b.productOptionTotalDiscountPrice &&
    a.productOptionTax === b.productOptionTax &&
    a.productOptionDiscountTax === b.productOptionDiscountTax
  );
}

export type ProductOptionMergeResult = {
  /** 사용자에게 보이는 값이 바뀌었거나(가격·이름 등) 옵션이 제외되었는지 */
  changed: boolean;
  /** 변경은 아니지만 저장값을 서버 값으로 채워야 하는지 */
  refreshed: boolean;
  removedCount: number;
  updatedCount: number;
  productOptions: ProductOption[];
};

/**
 * 현재 선택에 검증 결과를 적용한다. AVAILABLE만 남기고 서버 필드로 갱신하며
 * 기존 순서와 옵션별 기획전 아이디·GA 필드를 보존한다.
 */
export function mergeValidatedProductOptions(
  current: ProductOption[],
  rows: ProductOptionValidationRow[],
): ProductOptionMergeResult {
  const rowsById = new Map(rows.map((row) => [row.productOptionId, row]));
  const productOptions: ProductOption[] = [];
  let removedCount = 0;
  let updatedCount = 0;
  let refreshed = false;

  current.forEach((option) => {
    const row = rowsById.get(option.id);
    if (!row) {
      throw new InvalidProductOptionValidationResponseError('missing row');
    }
    if (row.status !== 'AVAILABLE' || !row.productOption) {
      removedCount += 1;
      return;
    }

    const next = mapValidatedProductOption(option, row.productOption);
    if (!isSameProductOption(option, next)) {
      updatedCount += 1;
    } else if (!hasSameServerFields(option, next)) {
      refreshed = true;
    }
    productOptions.push(next);
  });

  return {
    changed: removedCount > 0 || updatedCount > 0,
    refreshed,
    removedCount,
    updatedCount,
    productOptions,
  };
}
